import Phaser from 'phaser';
import { GAME_HEIGHT, JOYSTICK_BASE_RADIUS, JOYSTICK_KNOB_RADIUS } from '../config/game';

export class VirtualJoystick {
  private readonly base: Phaser.GameObjects.Arc;
  private readonly knob: Phaser.GameObjects.Arc;
  private readonly vector = new Phaser.Math.Vector2(0, 0);
  private basePosition = new Phaser.Math.Vector2(84, GAME_HEIGHT - 136);
  private activePointerId: number | null = null;

  constructor(private readonly scene: Phaser.Scene) {
    this.base = scene.add.circle(this.basePosition.x, this.basePosition.y, JOYSTICK_BASE_RADIUS, 0x2f2a22, 0.4);
    this.base.setStrokeStyle(2, 0xd8c28d, 0.54);
    this.base.setDepth(100);

    this.knob = scene.add.circle(this.basePosition.x, this.basePosition.y, JOYSTICK_KNOB_RADIUS, 0xe7b957, 0.6);
    this.knob.setStrokeStyle(2, 0xf7df9a, 0.82);
    this.knob.setDepth(101);
    
    // Hide initially for dynamic visual
    this.base.setAlpha(0);
    this.knob.setAlpha(0);

    scene.input.addPointer(2);
    scene.input.on('pointerdown', this.handlePointerDown, this);
    scene.input.on('pointermove', this.handlePointerMove, this);
    scene.input.on('pointerup', this.handlePointerUp, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  getVector() {
    return this.vector;
  }

  destroy() {
    this.scene.input.off('pointerdown', this.handlePointerDown, this);
    this.scene.input.off('pointermove', this.handlePointerMove, this);
    this.scene.input.off('pointerup', this.handlePointerUp, this);
    this.base.destroy();
    this.knob.destroy();
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer) {
    if (this.activePointerId !== null) {
      return;
    }

    // Only activate joystick on the bottom 70% of the screen to avoid HUD
    if (pointer.y < GAME_HEIGHT * 0.25) {
      return; 
    }

    this.activePointerId = pointer.id;
    this.basePosition.set(pointer.x, pointer.y);
    this.base.setPosition(this.basePosition.x, this.basePosition.y);
    this.knob.setPosition(this.basePosition.x, this.basePosition.y);
    
    this.scene.tweens.killTweensOf([this.base, this.knob]);
    this.scene.tweens.add({
      targets: [this.base, this.knob],
      alpha: 1,
      duration: 100
    });

    this.updateFromPointer(pointer);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer) {
    if (pointer.id !== this.activePointerId) {
      return;
    }
    this.updateFromPointer(pointer);
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer) {
    if (pointer.id !== this.activePointerId) {
      return;
    }

    this.activePointerId = null;
    this.vector.set(0, 0);
    this.knob.setPosition(this.basePosition.x, this.basePosition.y);

    this.scene.tweens.killTweensOf([this.base, this.knob]);
    this.scene.tweens.add({
      targets: [this.base, this.knob],
      alpha: 0,
      duration: 200
    });
  }

  private updateFromPointer(pointer: Phaser.Input.Pointer) {
    const raw = new Phaser.Math.Vector2(pointer.x - this.basePosition.x, pointer.y - this.basePosition.y);
    const distance = raw.length();
    const clampedDistance = Math.min(distance, JOYSTICK_BASE_RADIUS);

    if (distance <= 1) {
      this.vector.set(0, 0);
      this.knob.setPosition(this.basePosition.x, this.basePosition.y);
      return;
    }

    raw.normalize();
    this.vector.copy(raw).scale(clampedDistance / JOYSTICK_BASE_RADIUS);
    this.knob.setPosition(
      this.basePosition.x + raw.x * clampedDistance,
      this.basePosition.y + raw.y * clampedDistance,
    );
  }
}
